/**
 * StaticDataService.ts — IDataService implementation backed by bundled JSON
 *
 * Loads all catalog JSON files at init() via Vite static imports (bundled
 * into the tool artifact, zero runtime fetch). Builds in-memory indexes
 * per locale with editorial content pre-merged by id.
 *
 * Derives `problemi_fondamentali_effettivi` once during init() from the
 * union of each author's written tappe tags (ADR 0003), with fallback to
 * `problemi_fondamentali_iniziali` when no tappe exist for an author.
 *
 * Supports bootstrap state where the catalog JSONs are empty arrays
 * (Fase C not yet executed): every query returns empty results without
 * throwing. Referential integrity warnings are logged via console.warn
 * but never fail init().
 *
 * Locale fallback: if an entity has no editorial content for the requested
 * locale, falls back to italian (FALLBACK_LOCALE) with console.warn. If
 * italian is also missing, returns empty string fields.
 */

import type {
  Argomento,
  ArgomentoContent,
  ArgomentoLocalized,
  Citazione,
  CitazioneContent,
  Concept,
  ConceptContent,
  ConceptLocalized,
  Philosopher,
  PhilosopherContent,
  PhilosopherLocalized,
  ProblemaFondamentale,
  Tappa,
  TappaContent,
  TappaLocalized,
  AnnoScolastico,
} from '@/data/schema';
import type { Locale } from '@/i18n/locale';
import type {
  AttributedCitation,
  IDataService,
  PhilosopherFilters,
  SearchResult,
  TappaFilters,
} from './IDataService';

// ══════ Raw JSON imports (bundled by Vite) ══════
import autoriRaw from '@/data/autori.json';
import concettiRaw from '@/data/concetti.json';
import tappeRaw from '@/data/tappe.json';
import argomentiRaw from '@/data/argomenti.json';

import autoriItRaw from '@/data/autori.it.json';
import autoriEnRaw from '@/data/autori.en.json';
import concettiItRaw from '@/data/concetti.it.json';
import concettiEnRaw from '@/data/concetti.en.json';
import tappeItRaw from '@/data/tappe.it.json';
import tappeEnRaw from '@/data/tappe.en.json';
import argomentiItRaw from '@/data/argomenti.it.json';
import argomentiEnRaw from '@/data/argomenti.en.json';

// ══════ Constants ══════

const SUPPORTED_LOCALES: readonly Locale[] = ['it', 'en'] as const;
const FALLBACK_LOCALE: Locale = 'it';
const DEFAULT_SEARCH_LIMIT = 20;

// Search scoring weights
const SCORE_EXACT_NAME = 100;
const SCORE_WORD_START = 60;
const SCORE_SUBSTRING_NAME = 30;
const SCORE_SUBSTRING_SHORT_PROSE = 10;
const SCORE_SUBSTRING_LONG_PROSE = 5;

// ══════ Implementation ══════

export class StaticDataService implements IDataService {
  private initialized = false;

  // Language-neutral base catalog (single source of truth for structural data)
  private authorsBase: Philosopher[] = [];
  private conceptsBase: Concept[] = [];
  private tappeBase: Tappa[] = [];
  private argomentiBase: Argomento[] = [];

  // Localized indexes (id → entity with content merged for a specific locale)
  private authorsByLocale = new Map<Locale, Map<string, PhilosopherLocalized>>();
  private conceptsByLocale = new Map<Locale, Map<string, ConceptLocalized>>();
  private tappeByLocale = new Map<Locale, Map<string, TappaLocalized>>();
  private argomentiByLocale = new Map<Locale, Map<string, ArgomentoLocalized>>();

  async init(): Promise<void> {
    if (this.initialized) return;

    // Cast raw JSON (empty arrays at bootstrap; populated after Fase C)
    this.authorsBase = autoriRaw as unknown as Philosopher[];
    this.conceptsBase = concettiRaw as unknown as Concept[];
    this.tappeBase = tappeRaw as unknown as Tappa[];
    this.argomentiBase = argomentiRaw as unknown as Argomento[];

    const authorContent: Record<Locale, PhilosopherContent[]> = {
      it: autoriItRaw as unknown as PhilosopherContent[],
      en: autoriEnRaw as unknown as PhilosopherContent[],
    };
    const conceptContent: Record<Locale, ConceptContent[]> = {
      it: concettiItRaw as unknown as ConceptContent[],
      en: concettiEnRaw as unknown as ConceptContent[],
    };
    const tappaContent: Record<Locale, TappaContent[]> = {
      it: tappeItRaw as unknown as TappaContent[],
      en: tappeEnRaw as unknown as TappaContent[],
    };
    const argomentoContent: Record<Locale, ArgomentoContent[]> = {
      it: argomentiItRaw as unknown as ArgomentoContent[],
      en: argomentiEnRaw as unknown as ArgomentoContent[],
    };

    this.validateIntegrity();

    // Build tappe first — authors derivation depends on them
    for (const locale of SUPPORTED_LOCALES) {
      this.tappeByLocale.set(locale, this.buildTappeLocalized(locale, tappaContent));
    }
    for (const locale of SUPPORTED_LOCALES) {
      this.authorsByLocale.set(locale, this.buildAuthorsLocalized(locale, authorContent));
    }
    for (const locale of SUPPORTED_LOCALES) {
      this.conceptsByLocale.set(locale, this.buildConceptsLocalized(locale, conceptContent));
    }
    for (const locale of SUPPORTED_LOCALES) {
      this.argomentiByLocale.set(locale, this.buildArgomentiLocalized(locale, argomentoContent));
    }

    this.initialized = true;
  }

  // ──────── Integrity check ────────

  private validateIntegrity(): void {
    // Skip on bootstrap (empty catalog) — no entities to cross-check
    if (this.authorsBase.length === 0 && this.tappeBase.length === 0) return;

    const authorIds = new Set(this.authorsBase.map((a) => a.id));
    const conceptIds = new Set(this.conceptsBase.map((c) => c.id));
    const argomentoIds = new Set(this.argomentiBase.map((a) => a.id));
    const tappaIds = new Set(this.tappeBase.map((t) => t.id));

    for (const tappa of this.tappeBase) {
      if (!authorIds.has(tappa.autore_id)) {
        console.warn(
          `[StaticDataService] tappa "${tappa.id}" references unknown autore_id "${tappa.autore_id}"`,
        );
      }
      if (!conceptIds.has(tappa.concetto_id)) {
        console.warn(
          `[StaticDataService] tappa "${tappa.id}" references unknown concetto_id "${tappa.concetto_id}"`,
        );
      }
      if (tappa.argomento_id && !argomentoIds.has(tappa.argomento_id)) {
        console.warn(
          `[StaticDataService] tappa "${tappa.id}" references unknown argomento_id "${tappa.argomento_id}"`,
        );
      }
    }

    for (const concept of this.conceptsBase) {
      for (const tId of concept.tappe_ordinate) {
        if (!tappaIds.has(tId)) {
          console.warn(
            `[StaticDataService] concept "${concept.id}" tappe_ordinate references unknown tappa "${tId}"`,
          );
        }
      }
    }

    for (const arg of this.argomentiBase) {
      if (!authorIds.has(arg.autore_id)) {
        console.warn(
          `[StaticDataService] argomento "${arg.id}" references unknown autore_id "${arg.autore_id}"`,
        );
      }
      for (const cId of arg.concetti_toccati) {
        if (!conceptIds.has(cId)) {
          console.warn(
            `[StaticDataService] argomento "${arg.id}" concetti_toccati references unknown concetto "${cId}"`,
          );
        }
      }
    }
  }

  // ──────── Build phase: merge base + editorial per locale ────────

  private buildTappeLocalized(
    locale: Locale,
    contentByLocale: Record<Locale, TappaContent[]>,
  ): Map<string, TappaLocalized> {
    const contentIndex = this.indexById(contentByLocale[locale]);
    const fallbackIndex =
      locale !== FALLBACK_LOCALE ? this.indexById(contentByLocale[FALLBACK_LOCALE]) : null;

    const map = new Map<string, TappaLocalized>();
    for (const tappa of this.tappeBase) {
      const content = this.resolveContent(
        tappa.id,
        contentIndex,
        fallbackIndex,
        locale,
        'tappa',
        () => this.emptyTappaContent(tappa),
      );
      map.set(tappa.id, { ...tappa, content });
    }
    return map;
  }

  private buildAuthorsLocalized(
    locale: Locale,
    contentByLocale: Record<Locale, PhilosopherContent[]>,
  ): Map<string, PhilosopherLocalized> {
    const contentIndex = this.indexById(contentByLocale[locale]);
    const fallbackIndex =
      locale !== FALLBACK_LOCALE ? this.indexById(contentByLocale[FALLBACK_LOCALE]) : null;

    const tappeMap = this.tappeByLocale.get(locale) ?? new Map<string, TappaLocalized>();

    const map = new Map<string, PhilosopherLocalized>();
    for (const author of this.authorsBase) {
      const content = this.resolveContent(
        author.id,
        contentIndex,
        fallbackIndex,
        locale,
        'author',
        () => ({ id: author.id, inquadramento: '', contesto_generale: '' }),
      );

      const effettivi = this.deriveProblemiEffettivi(author, tappeMap);

      map.set(author.id, {
        ...author,
        content,
        problemi_fondamentali_effettivi: effettivi,
      });
    }
    return map;
  }

  private buildConceptsLocalized(
    locale: Locale,
    contentByLocale: Record<Locale, ConceptContent[]>,
  ): Map<string, ConceptLocalized> {
    const contentIndex = this.indexById(contentByLocale[locale]);
    const fallbackIndex =
      locale !== FALLBACK_LOCALE ? this.indexById(contentByLocale[FALLBACK_LOCALE]) : null;

    const map = new Map<string, ConceptLocalized>();
    for (const concept of this.conceptsBase) {
      const content = this.resolveContent(
        concept.id,
        contentIndex,
        fallbackIndex,
        locale,
        'concept',
        () => ({ id: concept.id, descrizione: '' }),
      );
      map.set(concept.id, { ...concept, content });
    }
    return map;
  }

  private buildArgomentiLocalized(
    locale: Locale,
    contentByLocale: Record<Locale, ArgomentoContent[]>,
  ): Map<string, ArgomentoLocalized> {
    const contentIndex = this.indexById(contentByLocale[locale]);
    const fallbackIndex =
      locale !== FALLBACK_LOCALE ? this.indexById(contentByLocale[FALLBACK_LOCALE]) : null;

    const map = new Map<string, ArgomentoLocalized>();
    for (const arg of this.argomentiBase) {
      const content = this.resolveContent(
        arg.id,
        contentIndex,
        fallbackIndex,
        locale,
        'argomento',
        () => ({
          id: arg.id,
          enunciato: '',
          inquadramento: '',
          argomento_passo_passo: { premesse: [], conclusione: '' },
          punto_decisivo: '',
          obiezioni_storiche: [],
          eredita: '',
          citazioni: [],
        }),
      );
      map.set(arg.id, { ...arg, content });
    }
    return map;
  }

  // ──────── Build helpers ────────

  private indexById<T extends { id: string }>(items: T[]): Map<string, T> {
    const map = new Map<string, T>();
    for (const item of items) map.set(item.id, item);
    return map;
  }

  private resolveContent<T>(
    id: string,
    contentIndex: Map<string, T>,
    fallbackIndex: Map<string, T> | null,
    locale: Locale,
    entityKind: string,
    emptyFactory: () => T,
  ): T {
    const found = contentIndex.get(id);
    if (found) return found;

    if (fallbackIndex) {
      const fallback = fallbackIndex.get(id);
      if (fallback) {
        console.warn(
          `[StaticDataService] ${entityKind} "${id}" missing content for locale "${locale}", falling back to "${FALLBACK_LOCALE}"`,
        );
        return fallback;
      }
    }

    console.warn(
      `[StaticDataService] ${entityKind} "${id}" has no content in any locale, using empty placeholder`,
    );
    return emptyFactory();
  }

  private emptyTappaContent(tappa: Tappa): TappaContent {
    return {
      id: tappa.id,
      spiegazione: '',
      contesto_storico: '',
      cosa_cambia: '',
      citazioni: tappa.citazioni.map((c) => ({
        id: c.id,
        testo_originale: '',
        testo_tradotto: '',
      })),
    };
  }

  private deriveProblemiEffettivi(
    author: Philosopher,
    tappeMap: Map<string, TappaLocalized>,
  ): ProblemaFondamentale[] {
    const authorTappe: TappaLocalized[] = [];
    for (const t of tappeMap.values()) {
      if (t.autore_id === author.id) authorTappe.push(t);
    }
    if (authorTappe.length === 0) {
      return [...author.problemi_fondamentali_iniziali];
    }
    const set = new Set<ProblemaFondamentale>();
    for (const t of authorTappe) set.add(t.problema_fondamentale);
    return Array.from(set);
  }

  private ensureInit(): void {
    if (!this.initialized) {
      throw new Error(
        '[StaticDataService] init() must be awaited before calling any other method',
      );
    }
  }

  // ══════ PHILOSOPHERS ══════

  async getAuthor(id: string, locale: Locale): Promise<PhilosopherLocalized | null> {
    this.ensureInit();
    return this.authorsByLocale.get(locale)?.get(id) ?? null;
  }

  async listAuthors(
    filters: PhilosopherFilters,
    locale: Locale,
  ): Promise<PhilosopherLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.authorsByLocale.get(locale)?.values() ?? []);
    const filtered = all.filter((a) => this.matchesAuthorFilters(a, filters));
    filtered.sort((a, b) => a.anno_nascita - b.anno_nascita);
    return filtered;
  }

  private matchesAuthorFilters(
    author: PhilosopherLocalized,
    f: PhilosopherFilters,
  ): boolean {
    if (f.anno_scolastico !== undefined && !author.anno_scolastico.includes(f.anno_scolastico)) {
      return false;
    }
    if (f.solo_obbligatori === true && !author.obbligatorio_ministero) {
      return false;
    }
    if (
      f.problema_fondamentale !== undefined &&
      !author.problemi_fondamentali_effettivi.includes(f.problema_fondamentale)
    ) {
      return false;
    }
    if (f.anno_nascita_min !== undefined && author.anno_nascita < f.anno_nascita_min) {
      return false;
    }
    if (f.anno_nascita_max !== undefined && author.anno_nascita > f.anno_nascita_max) {
      return false;
    }
    return true;
  }

  async getAuthorsByProblema(
    problema: ProblemaFondamentale,
    locale: Locale,
  ): Promise<PhilosopherLocalized[]> {
    return this.listAuthors({ problema_fondamentale: problema }, locale);
  }

  async getObbligatoriByAnno(
    anno: AnnoScolastico,
    locale: Locale,
  ): Promise<PhilosopherLocalized[]> {
    return this.listAuthors({ anno_scolastico: anno, solo_obbligatori: true }, locale);
  }

  // ══════ CONCEPTS ══════

  async getConcept(id: string, locale: Locale): Promise<ConceptLocalized | null> {
    this.ensureInit();
    return this.conceptsByLocale.get(locale)?.get(id) ?? null;
  }

  async listConcepts(locale: Locale): Promise<ConceptLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.conceptsByLocale.get(locale)?.values() ?? []);
    all.sort((a, b) => a.id.localeCompare(b.id));
    return all;
  }

  async getConceptsByProblema(
    problema: ProblemaFondamentale,
    locale: Locale,
  ): Promise<ConceptLocalized[]> {
    const all = await this.listConcepts(locale);
    return all.filter((c) => c.problema_fondamentale_associato === problema);
  }

  // ══════ TAPPE ══════

  async getTappa(id: string, locale: Locale): Promise<TappaLocalized | null> {
    this.ensureInit();
    return this.tappeByLocale.get(locale)?.get(id) ?? null;
  }

  async getTappeByAuthor(autore_id: string, locale: Locale): Promise<TappaLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.tappeByLocale.get(locale)?.values() ?? []);
    const filtered = all.filter((t) => t.autore_id === autore_id);
    filtered.sort((a, b) => a.anno_riferimento - b.anno_riferimento);
    return filtered;
  }

  async getTappeByConcept(concetto_id: string, locale: Locale): Promise<TappaLocalized[]> {
    this.ensureInit();
    const concept = this.conceptsByLocale.get(locale)?.get(concetto_id);
    if (!concept) return [];
    const tappeMap = this.tappeByLocale.get(locale);
    if (!tappeMap) return [];

    const result: TappaLocalized[] = [];
    for (const tId of concept.tappe_ordinate) {
      const t = tappeMap.get(tId);
      if (t) result.push(t);
    }
    return result;
  }

  async getTappeByArgomento(argomento_id: string, locale: Locale): Promise<TappaLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.tappeByLocale.get(locale)?.values() ?? []);
    return all.filter((t) => t.argomento_id === argomento_id);
  }

  async listTappe(filters: TappaFilters, locale: Locale): Promise<TappaLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.tappeByLocale.get(locale)?.values() ?? []);
    return all.filter((t) => {
      if (filters.autore_id !== undefined && t.autore_id !== filters.autore_id) return false;
      if (filters.concetto_id !== undefined && t.concetto_id !== filters.concetto_id) return false;
      if (
        filters.problema_fondamentale !== undefined &&
        t.problema_fondamentale !== filters.problema_fondamentale
      ) {
        return false;
      }
      if (
        filters.anno_riferimento_min !== undefined &&
        t.anno_riferimento < filters.anno_riferimento_min
      ) {
        return false;
      }
      if (
        filters.anno_riferimento_max !== undefined &&
        t.anno_riferimento > filters.anno_riferimento_max
      ) {
        return false;
      }
      return true;
    });
  }

  // ══════ ARGOMENTI ══════

  async getArgomento(id: string, locale: Locale): Promise<ArgomentoLocalized | null> {
    this.ensureInit();
    return this.argomentiByLocale.get(locale)?.get(id) ?? null;
  }

  async getArgomentiByAuthor(
    autore_id: string,
    locale: Locale,
  ): Promise<ArgomentoLocalized[]> {
    this.ensureInit();
    const all = Array.from(this.argomentiByLocale.get(locale)?.values() ?? []);
    return all.filter((a) => a.autore_id === autore_id);
  }

  // ══════ SEARCH ══════

  async search(
    query: string,
    locale: Locale,
    limit: number = DEFAULT_SEARCH_LIMIT,
  ): Promise<SearchResult[]> {
    this.ensureInit();
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [];

    const results: SearchResult[] = [];

    const authors = this.authorsByLocale.get(locale);
    if (authors) {
      for (const a of authors.values()) {
        const score = this.scoreAuthor(a, q);
        if (score > 0) results.push({ kind: 'philosopher', item: a, score });
      }
    }

    const concepts = this.conceptsByLocale.get(locale);
    if (concepts) {
      for (const c of concepts.values()) {
        const score = this.scoreConcept(c, q);
        if (score > 0) results.push({ kind: 'concept', item: c, score });
      }
    }

    const tappe = this.tappeByLocale.get(locale);
    if (tappe) {
      for (const t of tappe.values()) {
        const score = this.scoreTappa(t, q);
        if (score > 0) results.push({ kind: 'tappa', item: t, score });
      }
    }

    const argomenti = this.argomentiByLocale.get(locale);
    if (argomenti) {
      for (const a of argomenti.values()) {
        const score = this.scoreArgomento(a, q);
        if (score > 0) results.push({ kind: 'argomento', item: a, score });
      }
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, limit);
  }

  private scoreName(name: string, id: string, q: string): number {
    const nameLower = name.toLowerCase();
    if (nameLower === q || id === q) return SCORE_EXACT_NAME;
    if (nameLower.startsWith(q) || this.containsWordStart(nameLower, q)) return SCORE_WORD_START;
    if (nameLower.includes(q)) return SCORE_SUBSTRING_NAME;
    return 0;
  }

  private containsWordStart(text: string, q: string): boolean {
    const words = text.split(/\s+/);
    return words.some((w) => w.startsWith(q));
  }

  private scoreAuthor(a: PhilosopherLocalized, q: string): number {
    let score = this.scoreName(a.nome, a.id, q);
    if (score === 0 && a.content.inquadramento.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_SHORT_PROSE;
    }
    if (score === 0 && a.content.contesto_generale.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_LONG_PROSE;
    }
    return score;
  }

  private scoreConcept(c: ConceptLocalized, q: string): number {
    let score = this.scoreName(c.nome, c.id, q);
    if (score === 0 && c.content.descrizione.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_SHORT_PROSE;
    }
    return score;
  }

  private scoreTappa(t: TappaLocalized, q: string): number {
    if (t.id.toLowerCase().includes(q)) return SCORE_SUBSTRING_NAME;
    if (t.content.spiegazione.toLowerCase().includes(q)) return SCORE_SUBSTRING_SHORT_PROSE;
    if (t.content.contesto_storico.toLowerCase().includes(q)) return SCORE_SUBSTRING_LONG_PROSE;
    if (t.content.cosa_cambia.toLowerCase().includes(q)) return SCORE_SUBSTRING_LONG_PROSE;
    return 0;
  }

  private scoreArgomento(a: ArgomentoLocalized, q: string): number {
    let score = this.scoreName(a.nome, a.id, q);
    if (score === 0 && a.content.enunciato.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_NAME;
    }
    if (score === 0 && a.content.inquadramento.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_SHORT_PROSE;
    }
    if (score === 0 && a.content.punto_decisivo.toLowerCase().includes(q)) {
      score = SCORE_SUBSTRING_SHORT_PROSE;
    }
    return score;
  }

  // ══════ CREDITS / ATTRIBUTIONS ══════

  async listCitazioniConAttribuzione(locale: Locale): Promise<AttributedCitation[]> {
    this.ensureInit();
    const tappeMap = this.tappeByLocale.get(locale);
    if (!tappeMap) return [];

    const results: AttributedCitation[] = [];
    for (const tappa of tappeMap.values()) {
      const contentById = new Map<string, CitazioneContent>();
      for (const cc of tappa.content.citazioni) contentById.set(cc.id, cc);

      for (const cit of tappa.citazioni) {
        if (this.requiresAttribution(cit)) {
          const content = contentById.get(cit.id);
          if (content) {
            results.push({ tappa_id: tappa.id, citazione: cit, content });
          } else {
            console.warn(
              `[StaticDataService] citazione "${cit.id}" in tappa "${tappa.id}" missing content for locale "${locale}"`,
            );
          }
        }
      }
    }
    return results;
  }

  private requiresAttribution(cit: Citazione): boolean {
    return cit.license === 'CC-BY' || cit.license === 'CC-BY-SA' || cit.license === 'CC0';
  }
}
